import React, { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import * as DropdownMenuPrimitive from '@radix-ui/react-dropdown-menu';
import {
  Clapperboard,
  Edit,
  GalleryHorizontal,
  Image as ImageIcon,
  Lightbulb,
  MoreHorizontal,
  Share2,
  Sparkles,
  ThumbsDown,
  ThumbsUp,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ProBadge } from '@/components/ProBadge';
import type { ClipCardMenuItem } from '@/lib/clips/clipCardActions';
import { cn } from '@/lib/utils';

export type ClipCardMenuAction = Exclude<ClipCardMenuItem, 'rating'>;
export type ClipCardRating = 'like' | 'dislike';

/** Items that open a dialog: the menu must not pull focus back to its trigger. */
const OPENS_DIALOG: ReadonlySet<ClipCardMenuAction> = new Set(['cover', 'titles', 'share']);

interface Props {
  groups: ClipCardMenuItem[][];
  rating: string;
  onAction: (action: ClipCardMenuAction) => void;
  onRate: (rating: ClipCardRating) => void;
}

const ratingItemClassName =
  'flex flex-1 cursor-pointer select-none items-center justify-center gap-1.5 whitespace-nowrap rounded-sm bg-secondary px-2 py-1.5 text-xs text-muted-foreground outline-none transition-colors focus:bg-accent focus:text-accent-foreground';

/** The "⋯" menu of a /clips card: every action that is not Baixar or the second button. */
export const ClipCardMoreMenu: React.FC<Props> = ({ groups, rating, onAction, onRate }) => {
  const { t } = useTranslation();
  const openingDialogRef = useRef(false);

  const items: Record<ClipCardMenuAction, { testId: string; label: string; icon: React.ReactNode; extra?: React.ReactNode }> = {
    edit: { testId: 'clip-card-menu-edit', label: t('clipCard.editVideo'), icon: <Edit /> },
    editWithAi: {
      testId: 'clip-edit-with-ai',
      label: t('aiAssistant.studio.editWithAi'),
      icon: <Sparkles className="text-primary" />,
    },
    studio: { testId: 'clip-freecut-studio-action', label: t('clipStudio.open'), icon: <Clapperboard /> },
    carousel: {
      testId: 'clip-quote-carousel-action',
      label: t('quoteCarousel.title'),
      icon: <GalleryHorizontal />,
      extra: <ProBadge className="ml-auto px-1.5 py-0 text-[9px] leading-4" />,
    },
    cover: { testId: 'clip-cover-action', label: t('clipCover.action'), icon: <ImageIcon /> },
    titles: {
      testId: 'clip-card-titles',
      label: t('clipCard.generateTitles'),
      icon: <Lightbulb className="text-green-500" />,
    },
    share: { testId: 'clip-card-share', label: t('shareClip.share'), icon: <Share2 /> },
  };

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          data-testid="clip-card-more-actions"
          aria-label={t('clipCard.moreActions')}
          className="h-9 w-9 shrink-0 p-0"
        >
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="z-[100] min-w-[14rem]"
        onCloseAutoFocus={(event) => {
          if (openingDialogRef.current) {
            openingDialogRef.current = false;
            event.preventDefault();
          }
        }}
      >
        {groups.map((group, index) => (
          <React.Fragment key={group.join('-')}>
            {index > 0 && <DropdownMenuSeparator />}
            {group.map((item) => {
              if (item === 'rating') {
                return (
                  <div key="rating" className="flex gap-1 p-1">
                    <DropdownMenuPrimitive.CheckboxItem
                      data-testid="clip-card-like"
                      checked={rating === 'like'}
                      className={cn(ratingItemClassName, 'data-[state=checked]:bg-green-600 data-[state=checked]:text-white')}
                      onSelect={(event) => {
                        event.preventDefault();
                        onRate('like');
                      }}
                    >
                      <ThumbsUp className="h-3.5 w-3.5" />
                      {t('clipCard.like')}
                    </DropdownMenuPrimitive.CheckboxItem>
                    <DropdownMenuPrimitive.CheckboxItem
                      data-testid="clip-card-dislike"
                      checked={rating === 'dislike'}
                      className={cn(ratingItemClassName, 'data-[state=checked]:bg-red-600 data-[state=checked]:text-white')}
                      onSelect={(event) => {
                        event.preventDefault();
                        onRate('dislike');
                      }}
                    >
                      <ThumbsDown className="h-3.5 w-3.5" />
                      {t('clipCard.dislike')}
                    </DropdownMenuPrimitive.CheckboxItem>
                  </div>
                );
              }
              const { testId, label, icon, extra } = items[item];
              return (
                <DropdownMenuItem
                  key={item}
                  data-testid={testId}
                  className="cursor-pointer gap-2 [&_svg]:h-4 [&_svg]:w-4"
                  onSelect={() => {
                    if (OPENS_DIALOG.has(item)) openingDialogRef.current = true;
                    onAction(item);
                  }}
                >
                  {icon}
                  <span>{label}</span>
                  {extra}
                </DropdownMenuItem>
              );
            })}
          </React.Fragment>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
